/**
 * @module pages/ThankYouPage
 * @description Review pages for the post-Gala thank-you postcards:
 * `#/thank-you` lists them; `#/thank-you/<slug>` shows front and back with
 * print and image download. Linked from Admin, not from public navigation:
 * these cards are mailed to guests, not published on the site.
 */
import { useRef, useState } from "react";
import { ThankYouBack, ThankYouFront } from "../components/ThankYouPostcard";
import { useSettings, useThankYouCards } from "../data";
import { hrefFor } from "../app/router";
import { PRINT_SIZES } from "../config/print";
import { usePrint } from "../lib/print/PrintService";
import { downloadBlob, renderElementToPng } from "../lib/outputEngine";
import { Icon } from "../components/Icons";

export function ThankYouPage({ slug }: { slug: string | null }) {
    const cards = useThankYouCards();
    const { printSize } = useSettings();
    const print = usePrint();
    const frontRef = useRef<HTMLElement>(null);
    const backRef = useRef<HTMLElement>(null);
    const [status, setStatus] = useState("");
    const card = slug ? cards.find((c) => c.slug === slug) : undefined;

    if (!card) {
        return (
            <section className="card-page">
                <p className="pc-kicker">Post-Gala</p>
                <h1 className="section-title">Thank-you postcards</h1>
                {slug && <p className="pf-error">That thank-you card isn't available.</p>}
                <ul className="gallery">
                    {cards.map((c) => (
                        <li key={c.id}>
                            <a href={hrefFor({ name: "thank-you", slug: c.slug })} className="gallery-link">
                                <ThankYouFront card={c} />
                            </a>
                            <p className="gallery-caption">{c.name}</p>
                        </li>
                    ))}
                </ul>
            </section>
        );
    }

    /** Saves one face as a PNG, surfacing failures instead of hiding them. */
    const download = async (ref: React.RefObject<HTMLElement | null>, face: string) => {
        if (!ref.current) return;
        try {
            setStatus("Rendering image…");
            downloadBlob(await renderElementToPng(ref.current), `afj-thank-you-${card.slug}-${face}.png`);
            setStatus("");
        } catch (error) {
            console.error("[ThankYouPage] Image export failed", error);
            setStatus(`Image export failed: ${error instanceof Error ? error.message : String(error)}`);
        }
    };

    return (
        <section className="card-page">
            <nav className="card-pager" aria-label="Thank-you postcards">
                <a href={hrefFor({ name: "thank-you", slug: null })}>← All thank-you cards</a>
                <span>{card.name}</span>
                <a href={hrefFor({ name: "admin" })}>Edit in Admin →</a>
            </nav>
            <p className="pc-kicker face-label">Front</p>
            <ThankYouFront ref={frontRef} card={card} className="postcard--feature" />
            <p className="pc-kicker face-label">Back</p>
            <ThankYouBack ref={backRef} card={card} className="postcard--feature" />
            <div className="output-bar">
                <div className="output-icons" role="toolbar" aria-label="Print and download this thank-you card">
                    <button
                        type="button"
                        className="icon-btn"
                        aria-label="Print front and back"
                        title="Print front and back"
                        onClick={() =>
                            print({
                                size: PRINT_SIZES[printSize],
                                pages: [
                                    { key: "front", node: <ThankYouFront card={card} /> },
                                    { key: "back", node: <ThankYouBack card={card} /> },
                                ],
                            })
                        }
                    >
                        <Icon name="print" />
                    </button>
                    <button type="button" className="icon-btn" aria-label="Download front image" title="Download front image" onClick={() => download(frontRef, "front")}>
                        <Icon name="download" />
                    </button>
                    <button type="button" className="icon-btn" aria-label="Download back image" title="Download back image" onClick={() => download(backRef, "back")}>
                        <Icon name="download" />
                    </button>
                </div>
                <span className="output-status" aria-live="polite">{status}</span>
            </div>
        </section>
    );
}
